<template>
    <div>
        <div class="sectionTitle">Pending Photos ({{ pendingPhotos.length }})</div>
        <div class="photoGridPending">
            <span :class="imgContainerClass(photo.id)" @click="toggleSelection(photo.id)" v-for="photo in pendingPhotos"
                :key="photo.id">
                <img :src="photo.url" class="gridImg">
                <div class="thumbDesc">pending</div>
            </span>
        </div>
    </div>

    <div>
        <div class="sectionTitle">Approved Photos ({{ approvedPhotos.length }})</div>
        <div class="photoGridApproved">
            <span v-for="photo in approvedPhotos" :key="photo.id">
                <div :class="imgContainerClass(photo.id)" @click="toggleSelection(photo.id)">
                    <img :src="photo.url" class="gridImg" />
                </div>
                <div class="thumbDesc">
                    <input type="checkbox" :id="'char-photo-' + photo.id" :checked="photo.isCharacterPhoto"
                        @click="onCharacterPhotoClicked(photo.id)" />
                    <label :for="'char-photo-' + photo.id"> Character Photo</label>
                </div>
            </span>
        </div>
    </div>

    <div v-if="showSelectionOptions" class="selectionOptions">
        <div>
            <div>
                {{ selectedPhotos.length }} of
                {{ approvedPhotos.length + pendingPhotos.length }}
                Photos Selected
            </div>
            <div>
                <v-btn color="green" @click="onApprovePhotos">Approve</v-btn>
                &nbsp;
                <v-btn color="orange" @click="onRejectPhotos">Reject</v-btn>
            </div>
        </div>
    </div>

</template>


<script>
import constants from '../../../photo-prov-shared/lib/classes/constants.mjs';

export default {
    name: 'PhotoGrid',

    props: {
        approvedPhotos: Array,
        pendingPhotos: Array,
    },

    data() {
        return {
            selectedPhotos: [],
        };
    },

    emits: [
        'photo-modified',
    ],

    computed: {
        showSelectionOptions() {
            return this.selectedPhotos.length > 0;
        },
    },

    methods: {
        imgContainerClass(photoId) {
            if (this.selectedPhotos.includes(photoId)) {
                return 'imgContainerSelected';
            }
            return 'imgContainer';
        },

        toggleSelection(photoId) {
            if (this.selectedPhotos.includes(photoId)) {
                this.selectedPhotos = this.selectedPhotos.filter(id => id !== photoId);
            } else {
                this.selectedPhotos.push(photoId);
            }
        },

        onCharacterPhotoClicked(photoId) {
            // debug... console.log(`onCharacterPhotoClicked -- ${photoId}`);
            // toggle current state for specified photo
            const newValue = !this.approvedPhotos.find(photo => photo.id === photoId).isCharacterPhoto;
            this.setCharacterPhoto(photoId, newValue);
        },

        async onApprovePhotos() {
            await this.setPhotoState(constants.PHOTO_APPROVAL_STATES.APPROVED);
        },
        async onRejectPhotos() {
            await this.setPhotoState(constants.PHOTO_APPROVAL_STATES.REJECTED);
        },

        async setCharacterPhoto(photoId, isCharacterPhoto) {
            const authToken = this.idToken;
            const apiEndpoint = process.env.VUE_APP_PRV_API + '/characterPhoto';

            try {
                //==============================================================
                // send updated character photo state to server
                //==============================================================
                const response = await fetch(apiEndpoint, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                    },
                    body: JSON.stringify({
                        photoIds: [photoId],
                        isCharacterPhoto
                    }),
                });

                if (!response.ok) {
                    // Handle HTTP errors (e.g., 4xx, 5xx)
                    const errorText = await response.text(); // Get error details if available
                    console.error('Fetch error:', response.status, errorText);
                    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
                }

                // const data = await response.json(); // Parse the JSON response body
                // debug... console.log(`fetch ${apiEndpoint}\nresponse data:\n`, data);

                //==============================================================
                // notify parent component of changes
                //==============================================================
                this.selectedPhotos = [];
                this.$emit('photo-modified');

            } catch (error) {
                // Handle network errors or errors during fetch/parsing
                console.error('Error fetching data:', error);
            }

        },

        async setPhotoState(newApprovalState) {
            const authToken = this.idToken;
            const apiEndpoint = process.env.VUE_APP_PRV_API + '/photoState';

            try {
                //==============================================================
                // validate params
                //==============================================================
                const validStates = [
                    constants.PHOTO_APPROVAL_STATES.PENDING,
                    constants.PHOTO_APPROVAL_STATES.APPROVED,
                    constants.PHOTO_APPROVAL_STATES.REJECTED,
                ]
                if (!validStates.includes(newApprovalState)) {
                    throw new Error(`setPhotoState -- Invalid approval state: ${newApprovalState}`);
                }

                //==============================================================
                // send updated photo approval state to server
                //==============================================================
                const response = await fetch(apiEndpoint, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                    },
                    body: JSON.stringify({
                        photoIds: this.selectedPhotos,
                        approvalState: newApprovalState
                    }),
                });

                if (!response.ok) {
                    // Handle HTTP errors (e.g., 4xx, 5xx)
                    const errorText = await response.text(); // Get error details if available
                    console.error('Fetch error:', response.status, errorText);
                    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
                }

                const data = await response.json(); // Parse the JSON response body
                // debug...
                console.log(`fetch ${apiEndpoint}\nresponse data:\n`, data);

                //==============================================================
                // undo selections and
                // notify parent component of changes
                //==============================================================
                this.selectedPhotos = [];
                this.$emit('photo-modified');

            } catch (error) {
                // Handle network errors or errors during fetch/parsing
                console.error('Error fetching data:', error);
            }
        },

    },



};
</script>

<style scoped>
.photoGridApproved,
.photoGridPending {
    border: 0px dotted orange;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 10px;
}

.photoGridApproved {
    background-color: lightgreen;
}

.photoGridPending {
    background-color: #999;
}

.sectionTitle {
    text-align: center;
    font-weight: bold;
}

.imgContainerSelected,
.imgContainer {
    aspect-ratio: 1/1;
    padding: 10px;
    cursor: pointer;
}

.imgContainerSelected {
    border: 10px dotted whitesmoke;
    margin: 10px;
}

.gridImg {
    object-fit: contain;
    width: 100%;
    height: 100%;
}

.thumbDesc {
    text-align: center;
    color: #333;
}

.selectionOptions {
    position: fixed;
    bottom: 25px;
    left: 0;
    right: 0;
    z-index: 5;
    line-height: 1.5;
    max-height: 25vh;
    min-height: 10vh;
    background-color: whitesmoke;
    font-weight: bolder;
    text-align: center;
    vertical-align: middle;
    padding-top: 10px;
    /* x-offset | y-offset | blur | color */
    box-shadow: 0 -3px 6px rgba(0, 2, 0, 0.2);
}
</style>
